import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BadgeCheck,
  Eye,
  EyeOff,
  FileSignature,
  History,
  ImageIcon,
  KeyRound,
  Lock,
  Pencil,
  Save,
  ShieldAlert,
  Upload,
  UserX,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import type { CertificadoCargado, EstadoDeLaFirma, Firmante, Margenes, Membrete } from './contrato';
import {
  areaEnPorcentaje,
  consumido,
  diasRestantes,
  enFecha,
  estadoDeVigencia,
  type EstadoDeVigencia,
} from './vigencia';

const CLAVE = ['firma-electronica'];

export function PaginaFirmaElectronica() {
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const gestiona = puede('firma.gestionar');
  const [subiendo, setSubiendo] = useState(false);
  const [editandoCargo, setEditandoCargo] = useState(false);
  const [retirando, setRetirando] = useState(false);
  const [prueba, setPrueba] = useState<{ fichero: Blob | null } | null>(null);

  const estado = useQuery({
    queryKey: CLAVE,
    queryFn: () => api.get<EstadoDeLaFirma>('/firma-electronica'),
  });

  function refrescar() {
    void cliente.invalidateQueries({ queryKey: CLAVE });
  }

  const retirar = useMutation({
    mutationFn: () => api.post<void>('/firma-electronica/firmante/retirar'),
    onSuccess: () => {
      setRetirando(false);
      refrescar();
      notificar.exito('Certificado retirado: ya no se firma con él');
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo retirar'),
  });

  async function probar() {
    setPrueba({ fichero: null });
    try {
      const fichero = await api.descargar('/firma-electronica/prueba');
      setPrueba((actual) => (actual ? { fichero } : actual));
    } catch (causa) {
      setPrueba(null);
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo generar la prueba');
    }
  }

  const datos = estado.data;
  const firmante = datos?.firmante ?? null;

  return (
    <>
      <EncabezadoDePagina
        icono={FileSignature}
        color="esmeralda"
        titulo="Firma electrónica"
        subtitulo="Certificado del funcionario y hoja membretada de los certificados de no adeudar"
        acciones={
          gestiona && (
            <button
              type="button"
              onClick={() => void probar()}
              disabled={!datos}
              className="flex items-center gap-2 rounded-xl bg-marca-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-marca-700 disabled:opacity-50"
            >
              <BadgeCheck className="size-4" aria-hidden />
              Probar firma
            </button>
          )
        }
      />

      <div className="mb-5 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <Lock className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">Firma PAdES, la que valida FirmaEC.</span> El certificado
          y su contraseña se guardan cifrados y nunca vuelven a mostrarse. Antes de guardarlo se
          comprueba que esté vigente y que firme de verdad. «Probar firma» genera un certificado de
          ejemplo firmado: descárguelo y ábralo en FirmaEC para confirmar que lo reconoce.
        </p>
      </div>

      {estado.isPending || !datos ? (
        <Cargando titulo="Cargando la firma" detalle="Certificado y membrete" alto="h-48" />
      ) : (
        <div className="grid gap-6 xl:grid-cols-2">
          <FichaDelCertificado
            firmante={firmante}
            historial={datos.historial}
            gestiona={gestiona}
            alSubir={() => setSubiendo(true)}
            alEditarCargo={() => setEditandoCargo(true)}
            alRetirar={() => setRetirando(true)}
          />
          <FichaDelMembrete

            key={`${datos.membrete?.id ?? 'sin'}-${datos.membrete?.actualizadoEn ?? ''}`}
            membrete={datos.membrete ?? null}
            porDefecto={datos.margenesPorDefecto}
            gestiona={gestiona}
            alGuardar={refrescar}
          />
        </div>
      )}

      {subiendo && (
        <DialogoDeCertificado
          cargoActual={firmante?.cargo ?? ''}
          reemplaza={firmante}
          alCerrar={() => setSubiendo(false)}
          alGuardar={refrescar}
        />
      )}

      {editandoCargo && firmante && (
        <DialogoDeCargo
          firmante={firmante}
          alCerrar={() => setEditandoCargo(false)}
          alGuardar={refrescar}
        />
      )}

      <Modal
        abierto={retirando}
        alCerrar={() => setRetirando(false)}
        titulo="Retirar certificado"
        icono={UserX}
        ancho="estrecho"
      >
        <div className="space-y-3 text-sm text-armazon-700">
          <p>
            Dejará de firmarse con el certificado de{' '}
            <span className="font-semibold uppercase text-armazon-900">{firmante?.titular}</span>.
          </p>
          <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs uppercase text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            Hasta que se suba otro, no se podrá emitir ningún certificado de no adeudar. Si ya tiene
            el del reemplazo, súbalo directamente: el actual se retira solo.
          </p>
        </div>
        <div className="mt-6 flex justify-end gap-2 border-t border-armazon-100 pt-5">
          <button
            type="button"
            onClick={() => setRetirando(false)}
            className="rounded-lg px-4 py-2 text-sm font-semibold uppercase text-armazon-600 hover:bg-armazon-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => retirar.mutate()}
            disabled={retirar.isPending}
            className="flex items-center gap-2 rounded-lg bg-alerta px-4 py-2 text-sm font-bold uppercase text-white hover:opacity-90 disabled:opacity-60"
          >
            <UserX className="size-4" aria-hidden />
            {retirar.isPending ? 'Retirando…' : 'Retirar'}
          </button>
        </div>
      </Modal>

      <VistaPreviaDeImpresion
        abierto={prueba !== null}
        alCerrar={() => setPrueba(null)}
        titulo="Certificado de ejemplo"
        subtitulo={
          firmante
            ? `Firmado por ${firmante.titular} · ábralo en FirmaEC para validarlo`
            : 'Sin firmar: todavía no hay certificado de firma'
        }
        fichero={prueba?.fichero ?? null}
        cargando={prueba !== null && prueba.fichero === null}
        textoDeCarga="Firmando el certificado de ejemplo…"
        formato="hoja"
        conBarraDelVisor
      />
    </>
  );
}

const VIGENCIA: Record<EstadoDeVigencia, { texto: string; insignia: string; barra: string }> = {
  vigente: { texto: 'Vigente', insignia: 'bg-marca-600 text-white', barra: 'bg-marca-500' },
  'por-caducar': {
    texto: 'Por caducar',
    insignia: 'bg-amber-500 text-white',
    barra: 'bg-amber-500',
  },
  caducado: { texto: 'Caducado', insignia: 'bg-alerta text-white', barra: 'bg-alerta' },
};

function FichaDelCertificado({
  firmante,
  historial,
  gestiona,
  alSubir,
  alEditarCargo,
  alRetirar,
}: {
  firmante: Firmante | null;
  historial: Firmante[];
  gestiona: boolean;
  alSubir: () => void;
  alEditarCargo: () => void;
  alRetirar: () => void;
}) {
  const [verHistorial, setVerHistorial] = useState(false);

  return (
    <section className="flex flex-col rounded-2xl border border-armazon-200 bg-white shadow-sm">
      <header className="flex items-start gap-4 border-b border-armazon-100 p-5">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-marca-50 text-marca-700 ring-1 ring-marca-100">
          <KeyRound className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold uppercase tracking-wide text-armazon-900">
            Certificado de firma
          </h2>
          <p className="mt-0.5 text-xs uppercase text-armazon-500">
            El .p12 personal del funcionario que firma los certificados
          </p>
        </div>
        {gestiona && (
          <button
            type="button"
            onClick={alSubir}
            className="flex shrink-0 items-center gap-2 rounded-lg bg-marca-600 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-marca-700"
          >
            <Upload className="size-4" aria-hidden />
            {firmante ? 'Reemplazar' : 'Subir'}
          </button>
        )}
      </header>

      <div className="flex-1 p-5">
        {firmante ? (
          <DatosDelFirmante
            firmante={firmante}
            gestiona={gestiona}
            alEditarCargo={alEditarCargo}
            alRetirar={alRetirar}
          />
        ) : (
          <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <ShieldAlert className="mt-0.5 size-5 shrink-0 text-amber-600" aria-hidden />
            <p className="text-xs uppercase leading-relaxed text-amber-900">
              <span className="font-semibold">Sin certificado de firma.</span> No se puede firmar
              ningún certificado de no adeudar hasta subir el .p12 del funcionario responsable.
            </p>
          </div>
        )}

        {historial.length > 0 && (
          <div className="mt-5">
            <button
              type="button"
              onClick={() => setVerHistorial((v) => !v)}
              aria-expanded={verHistorial}
              className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-armazon-400 hover:text-armazon-600"
            >
              <History className="size-3.5" aria-hidden />
              Certificados anteriores ({historial.length})
            </button>
            {verHistorial && (
              <ul className="mt-2 divide-y divide-armazon-100 rounded-xl border border-armazon-200">
                {historial.map((f) => (
                  <li key={f.id} className="p-3 text-xs uppercase">
                    <p className="font-semibold text-armazon-800">{f.titular}</p>
                    <p className="text-armazon-500">
                      {f.cargo} · válido hasta {enFecha(f.validoHasta)}
                    </p>
                    <p className="text-[11px] text-armazon-400">
                      Retirado el {f.retiradoEn ? enFecha(f.retiradoEn) : '—'}
                      {f.retiradoPor ? ` por ${f.retiradoPor}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function DatosDelFirmante({
  firmante: f,
  gestiona,
  alEditarCargo,
  alRetirar,
}: {
  firmante: Firmante;
  gestiona: boolean;
  alEditarCargo: () => void;
  alRetirar: () => void;
}) {
  const vigencia = estadoDeVigencia(f.validoHasta);
  const estilo = VIGENCIA[vigencia];
  const dias = diasRestantes(f.validoHasta);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${estilo.insignia}`}
        >
          {estilo.texto}
        </span>
        <span className="text-[11px] uppercase text-armazon-500">
          {vigencia === 'caducado'
            ? `Caducó el ${enFecha(f.validoHasta)}`
            : `Quedan ${dias} ${dias === 1 ? 'día' : 'días'}`}
        </span>
      </div>

      <p className="mt-3 text-lg font-bold uppercase leading-tight text-armazon-900">{f.titular}</p>
      <div className="mt-1 flex items-center gap-2">
        <p className="text-sm font-semibold uppercase text-marca-700">{f.cargo}</p>
        {gestiona && (
          <button
            type="button"
            onClick={alEditarCargo}
            title="Cambiar el cargo"
            aria-label="Cambiar el cargo"
            className="rounded-lg p-1 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
          >
            <Pencil className="size-3.5" aria-hidden />
          </button>
        )}
      </div>

      <div className="mt-4">
        <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-armazon-400">
          <span>Desde {enFecha(f.validoDesde)}</span>
          <span>Hasta {enFecha(f.validoHasta)}</span>
        </div>
        <div
          className="mt-1 h-2 overflow-hidden rounded-full bg-armazon-100"
          role="progressbar"
          aria-label="Vigencia consumida"
          aria-valuenow={Math.round(consumido(f.validoDesde, f.validoHasta))}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full rounded-full ${estilo.barra}`}
            style={{ width: `${consumido(f.validoDesde, f.validoHasta)}%` }}
          />
        </div>
      </div>

      <dl className="mt-4 grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
        <Dato nombre="Identificación" valor={f.identificacion ?? '—'} importe />
        <Dato nombre="Certificadora" valor={f.entidadEmisora} />
        <Dato nombre="N.º de serie" valor={f.numeroDeSerie} importe />
        <Dato
          nombre="Huella SHA-256"
          valor={`${f.huella.slice(0, 16)}…`}
          titulo={f.huella}
          importe
        />
        <Dato
          nombre="Cargado"
          valor={`${enFecha(f.cargadoEn)}${f.cargadoPor ? ` · ${f.cargadoPor}` : ''}`}
        />
      </dl>

      {vigencia !== 'vigente' && (
        <p className="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs uppercase text-amber-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          {vigencia === 'caducado'
            ? 'Ya no firma. Suba el certificado renovado.'
            : 'Tramite ya la renovación con la certificadora: al caducar no se podrá firmar.'}
        </p>
      )}

      {gestiona && (
        <button
          type="button"
          onClick={alRetirar}
          className="mt-5 flex items-center gap-2 rounded-lg border border-armazon-200 px-3 py-2 text-xs font-bold uppercase tracking-wide text-armazon-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-alerta"
        >
          <UserX className="size-4" aria-hidden />
          Retirar certificado
        </button>
      )}
    </>
  );
}

function Dato({
  nombre,
  valor,
  titulo,
  importe = false,
}: {
  nombre: string;
  valor: string;
  titulo?: string;
  importe?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold uppercase tracking-wider text-armazon-400">{nombre}</dt>
      <dd
        className={`truncate text-armazon-700 ${importe ? 'importe' : 'uppercase'}`}
        title={titulo ?? valor}
      >
        {valor}
      </dd>
    </div>
  );
}

function FichaDelMembrete({
  membrete,
  porDefecto,
  gestiona,
  alGuardar,
}: {
  membrete: Membrete | null;
  porDefecto: Margenes;
  gestiona: boolean;
  alGuardar: () => void;
}) {
  const guardados = membrete?.margenes ?? porDefecto;

  const [margenes, setMargenes] = useState<Record<keyof Margenes, string>>(() => ({
    superior: String(guardados.superior),
    inferior: String(guardados.inferior),
    izquierdo: String(guardados.izquierdo),
    derecho: String(guardados.derecho),
  }));
  const enNumeros: Margenes = {
    superior: Number(margenes.superior) || 0,
    inferior: Number(margenes.inferior) || 0,
    izquierdo: Number(margenes.izquierdo) || 0,
    derecho: Number(margenes.derecho) || 0,
  };
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const imagen = useQuery({
    queryKey: ['firma-electronica', 'membrete', membrete?.id],
    enabled: membrete !== null,
    queryFn: async () => {
      const blob = await api.descargar('/firma-electronica/membrete/imagen');
      return blob ? aUrlDeDatos(blob) : null;
    },
    staleTime: Infinity,
  });

  const cambiado = (Object.keys(guardados) as (keyof Margenes)[]).some(
    (k) => enNumeros[k] !== guardados[k],
  );

  async function subir(archivo: File | undefined) {
    if (!archivo) {
      return;
    }
    setSubiendo(true);
    const formulario = new FormData();
    formulario.append('imagen', archivo, archivo.name);
    try {
      await api.subir<Membrete>('/firma-electronica/membrete', formulario);
      alGuardar();
      notificar.exito('Membrete cargado');
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo subir el membrete');
    } finally {
      setSubiendo(false);
    }
  }

  async function guardarMargenes() {
    setGuardando(true);
    try {
      await api.put<Membrete>('/firma-electronica/membrete/margenes', enNumeros);
      alGuardar();
      notificar.exito('Márgenes guardados');
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudieron guardar');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <section className="flex flex-col rounded-2xl border border-armazon-200 bg-white shadow-sm">
      <header className="flex items-start gap-4 border-b border-armazon-100 p-5">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-marca-50 text-marca-700 ring-1 ring-marca-100">
          <ImageIcon className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold uppercase tracking-wide text-armazon-900">
            Hoja membretada
          </h2>
          <p className="mt-0.5 text-xs uppercase text-armazon-500">
            El fondo de cada certificado: PNG o JPEG, A4 vertical
          </p>
        </div>
        {gestiona && (
          <label
            className={`flex shrink-0 cursor-pointer items-center gap-2 rounded-lg bg-marca-600 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-marca-700 ${
              subiendo ? 'pointer-events-none opacity-60' : ''
            }`}
          >
            <Upload className="size-4" aria-hidden />
            {subiendo ? 'Subiendo…' : membrete ? 'Reemplazar' : 'Subir'}
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="sr-only"
              onChange={(e) => {
                void subir(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
        )}
      </header>

      <div className="grid flex-1 gap-5 p-5 sm:grid-cols-[minmax(0,14rem)_1fr]">

        <div className="relative mx-auto aspect-[210/297] w-full max-w-56 overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-armazon-200">
          {imagen.data ? (
            <img src={imagen.data} alt="Hoja membretada" className="size-full object-cover" />
          ) : (
            <div className="grid size-full place-items-center p-4 text-center text-[10px] uppercase text-armazon-400">
              {membrete ? 'Cargando…' : 'Sin membrete: los certificados saldrían en blanco'}
            </div>
          )}
          <div
            className="absolute border border-dashed border-blue-500 bg-blue-500/5"
            style={areaEnPorcentaje(enNumeros)}
            aria-hidden
          />
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-armazon-400">
            Área del texto (milímetros desde el borde)
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {(
              [
                ['superior', 'Arriba'],
                ['inferior', 'Abajo'],
                ['izquierdo', 'Izquierda'],
                ['derecho', 'Derecha'],
              ] as const
            ).map(([clave, nombre]) => (
              <Campo key={clave} etiqueta={nombre}>
                <input
                  type="number"
                  step="0.5"
                  min={0}
                  max={clave === 'superior' || clave === 'inferior' ? 120 : 60}
                  value={margenes[clave]}
                  disabled={!gestiona || !membrete}
                  onChange={(e) => setMargenes((m) => ({ ...m, [clave]: e.target.value }))}
                  className={`${claseEntrada} importe`}
                />
              </Campo>
            ))}
          </div>
          <p className="mt-3 text-[11px] uppercase leading-relaxed text-armazon-500">
            El recuadro azul es donde cae el texto. Déjelo entre la cabecera y el pie del membrete.
          </p>
          {membrete && (
            <p className="mt-2 text-[11px] uppercase text-armazon-400">
              Original {membrete.anchoOriginal} × {membrete.altoOriginal} px · en cada PDF{' '}
              {Math.round(membrete.pesoParaPdf / 1024)} KB
            </p>
          )}
          {gestiona && membrete && (
            <button
              type="button"
              onClick={() => void guardarMargenes()}
              disabled={!cambiado || guardando}
              className="mt-4 flex items-center gap-2 rounded-lg bg-marca-600 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-marca-700 disabled:opacity-50"
            >
              <Save className="size-4" aria-hidden />
              {guardando ? 'Guardando…' : 'Guardar márgenes'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function aUrlDeDatos(blob: Blob): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => resolver(String(lector.result));
    lector.onerror = () => rechazar(lector.error);
    lector.readAsDataURL(blob);
  });
}

function DialogoDeCertificado({
  cargoActual,
  reemplaza,
  alCerrar,
  alGuardar,
}: {
  cargoActual: string;
  reemplaza: Firmante | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [clave, setClave] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [cargo, setCargo] = useState(cargoActual);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const listo = archivo !== null && clave !== '' && cargo.trim() !== '';

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo) {
      return;
    }
    setError(null);
    setEnviando(true);
    const formulario = new FormData();
    formulario.append('archivo', archivo, archivo.name);
    formulario.append('clave', clave);
    formulario.append('cargo', cargo.trim());
    try {
      const cargado = await api.subir<CertificadoCargado>(
        '/firma-electronica/firmante',
        formulario,
      );
      alGuardar();
      alCerrar();
      notificar.exito(`Firmará ${cargado.firmante.titular}`, 'Certificado cargado');
      for (const aviso of cargado.avisos) {
        notificar.advertencia(aviso);
      }
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo cargar el certificado';
      setError(mensaje);
    } finally {

      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo={reemplaza ? 'Reemplazar certificado' : 'Subir certificado'}
      subtitulo="El .p12 o .pfx personal del funcionario que firma"
      icono={KeyRound}
      ancho="medio"
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={enviar} className="space-y-4" autoComplete="off">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm uppercase text-red-900"
          >
            {error}
          </p>
        )}
        {reemplaza && (
          <p className="rounded-lg border border-armazon-200 bg-armazon-50 p-3 text-xs uppercase text-armazon-600">
            El de <span className="font-semibold">{reemplaza.titular}</span> pasará al historial.
          </p>
        )}

        <Campo etiqueta="Archivo del certificado" requerido ayuda=".P12 O .PFX">
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-armazon-300 p-4 transition-colors hover:border-marca-400 hover:bg-marca-50/40">
            <KeyRound className="size-5 shrink-0 text-armazon-400" aria-hidden />
            <span className="min-w-0 truncate text-sm uppercase text-armazon-600">
              {archivo ? archivo.name : 'Elegir el archivo'}
            </span>
            <input
              type="file"
              accept=".p12,.pfx,application/x-pkcs12"
              className="sr-only"
              onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
            />
          </label>
        </Campo>

        <Campo etiqueta="Contraseña del certificado" requerido ayuda="SE GUARDA CIFRADA">
          <div className="relative">
            <input
              type={verClave ? 'text' : 'password'}
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              className={`${claseEntrada} pr-11`}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setVerClave((v) => !v)}
              aria-label={verClave ? 'Ocultar la contraseña' : 'Ver la contraseña'}
              className="absolute right-1 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-lg text-armazon-400 hover:bg-armazon-100 hover:text-armazon-700"
            >
              {verClave ? (
                <EyeOff className="size-4" aria-hidden />
              ) : (
                <Eye className="size-4" aria-hidden />
              )}
            </button>
          </div>
        </Campo>

        <Campo etiqueta="Cargo" requerido ayuda="EL QUE SALE BAJO LA FIRMA">
          <input
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
            className={`${claseEntrada} uppercase`}
            maxLength={150}
            placeholder="TESORERO"
          />
        </Campo>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || !listo}
          textoEnviar={enviando ? 'Comprobando…' : 'Cargar certificado'}
        />
      </form>
    </Modal>
  );
}

function DialogoDeCargo({
  firmante,
  alCerrar,
  alGuardar,
}: {
  firmante: Firmante;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const [cargo, setCargo] = useState(firmante.cargo);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.put<Firmante>('/firma-electronica/firmante/cargo', {
        id: firmante.id,
        cargo: cargo.trim(),
      });
      alGuardar();
      alCerrar();
      notificar.exito('Cargo actualizado');
    } catch (causa) {
      setError(causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Cambiar el cargo"
      subtitulo={firmante.titular}
      icono={Pencil}
      ancho="estrecho"
    >
      <form onSubmit={enviar} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm uppercase text-red-900"
          >
            {error}
          </p>
        )}
        <Campo etiqueta="Cargo" requerido ayuda="EL QUE SALE BAJO LA FIRMA">
          <input
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
            className={`${claseEntrada} uppercase`}
            maxLength={150}
          />
        </Campo>
        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || cargo.trim() === ''}
          textoEnviar="Guardar"
        />
      </form>
    </Modal>
  );
}
